import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper as MuiPaper,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon,
  School as SchoolIcon,
  Event as EventIcon,
  Book as BookIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as AssignmentIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { exportToExcel, exportToPDF } from '../services/exportService';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const extractId = (value) => {
  if (!value) return '';

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }

  if (typeof value === 'object') {
    return String(
      value._id ||
      value.id ||
      value.annee_id ||
      value.filiere_id ||
      value.niveau_id ||
      ''
    ).trim();
  }

  return '';
};

const compareIds = (id1, id2) => {
  const left = extractId(id1);
  const right = extractId(id2);
  return Boolean(left && right && left === right);
};

const dedupeFilieres = (list) => {
  const seen = new Set();
  const result = [];
  for (const f of list) {
    const key = extractId(f.intitule || f.nom || f.name) || extractId(f._id || f.filiere_id);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(f);
    }
  }
  return result;
};

const Inscriptions = () => {
  const utilisateur = JSON.parse(localStorage.getItem('user') || '{}');
  // La direction financière consulte les inscriptions en lecture seule.
  const peutModifierInscriptions = ['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'RESPONSABLE_SCOLARITE', 'CONSULTANT'].includes(utilisateur?.role);
  // État principal
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // États pour les dialogues
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingInscription, setEditingInscription] = useState(null);
  const [deletingInscription, setDeletingInscription] = useState(null);

  // États pour les données de référence
  const [etudiants, setEtudiants] = useState([]);
  const [anneesUniversitaires, setAnneesUniversitaires] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [niveauxFiltres, setNiveauxFiltres] = useState([]);
  const [loadingReferences, setLoadingReferences] = useState(false);
  const [etudiantSearch, setEtudiantSearch] = useState('');

  // États pour les filtres du tableau
  const [showFilters, setShowFilters] = useState(false);
  const [tableFilters, setTableFilters] = useState({
    annee_universitaire_id: '',
    filiere_id: '',
    niveau_id: '',
  });
  const [filieresFiltrees, setFilieresFiltrees] = useState([]);
  const [filieresForm, setFilieresForm] = useState([]);
  const [niveauxFiltresTable, setNiveauxFiltresTable] = useState([]);
  const [filteredInscriptions, setFilteredInscriptions] = useState([]);

  // État pour le formulaire d'affectation
  const [formData, setFormData] = useState({
    etudiant_id: '',
    annee_universitaire_id: '',
    filiere_id: '',
    niveau_id: '',
    statut: 'EN_ATTENTE',
  });

  const [formErrors, setFormErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  // État pour les notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================================
  // FONCTIONS DE NOTIFICATION
  // ============================================================
  const showNotification = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // ============================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================
  const loadReferences = useCallback(async () => {
    try {
      setLoadingReferences(true);
      const [etudiantsRes, anneesRes, filieresRes, niveauxRes] = await Promise.all([
        axios.get(`${API_URL}/etudiants`),
        axios.get(`${API_URL}/annees-universitaires`),
        axios.get(`${API_URL}/filieres`),
        axios.get(`${API_URL}/niveaux`),
      ]);

      const etudiantsList = etudiantsRes.data.data || etudiantsRes.data || [];
      const anneesList = anneesRes.data.data || anneesRes.data || [];
      const filieresList = filieresRes.data.data || filieresRes.data || [];
      const niveauxList = niveauxRes.data.data || niveauxRes.data || [];

      setEtudiants(etudiantsList);
      setAnneesUniversitaires(anneesList);
      setFilieres(filieresList);
      setNiveaux(niveauxList);

      // Sélectionner l'année active par défaut
      const anneeActive = anneesList.find(a => a.estActive === true);
      if (anneeActive) {
        setFormData(prev => ({
          ...prev,
          annee_universitaire_id: anneeActive._id || anneeActive.id
        }));
      }
    } catch (err) {
      console.error('Erreur chargement références:', err);
      showNotification('Erreur lors du chargement des références', 'error');
    } finally {
      setLoadingReferences(false);
    }
  }, []);

  const loadNiveauxByFiliere = useCallback(async (filiereId) => {
    if (!filiereId) {
      setNiveauxFiltres([]);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/filieres/${filiereId}/niveaux`);
      const niveauxList = response.data.data || response.data || [];
      setNiveauxFiltres(niveauxList);
      
      setFormData(prev => ({
        ...prev,
        niveau_id: ''
      }));
    } catch (err) {
      console.error('Erreur chargement niveaux:', err);
      setNiveauxFiltres([]);
    }
  }, []);

  const loadNiveauxTable = useCallback(async (filiereId) => {
    if (!filiereId) return [];
    try {
      const response = await axios.get(`${API_URL}/filieres/${filiereId}/niveaux`);
      return response.data.data || response.data || [];
    } catch (err) {
      return [];
    }
  }, []);

  const loadInscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Le backend renvoie toutes les inscriptions (il n'applique ni recherche ni pagination) :
      // la recherche texte et les filtres sont donc faits cote client. On charge tout d'un coup.
      const response = await axios.get(`${API_URL}/inscriptions`, { params: { limit: 100000 } });
      
      const data = response.data;
      if (Array.isArray(data)) {
        setInscriptions(data);
        setTotalCount(data.length);
      } else if (data?.data && Array.isArray(data.data)) {
        setInscriptions(data.data);
        setTotalCount(data.total || data.data.length);
      } else {
        setInscriptions([]);
        setTotalCount(0);
      }

    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors du chargement';
      setError(message);
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================
  // EFFETS
  // ============================================================
  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  useEffect(() => {
    loadInscriptions();
  }, [loadInscriptions]);

  useEffect(() => {
    if (formData.filiere_id) {
      loadNiveauxByFiliere(formData.filiere_id);
    } else {
      setNiveauxFiltres([]);
    }
  }, [formData.filiere_id, loadNiveauxByFiliere]);

  useEffect(() => {
    const anneeId = formData.annee_universitaire_id;
    if (!anneeId) {
      setFilieresForm([]);
      return;
    }
    const annee = anneesUniversitaires.find(a =>
      (a._id || a.id) === anneeId || a.annee_id === anneeId
    );
    if (!annee) {
      setFilieresForm([]);
      return;
    }
    const allowedAnneeIds = [annee._id, annee.annee_id, anneeId]
      .map(extractId)
      .filter(Boolean);
    const filteredList = dedupeFilieres(filieres.filter(f =>
      allowedAnneeIds.some(id => compareIds(f.annee_id, id))
    ));
    setFilieresForm(filteredList);
    if (formData.filiere_id) {
      const stillValid = filteredList.some(f =>
        (f._id || f.id) === formData.filiere_id || f.filiere_id === formData.filiere_id
      );
      if (!stillValid) {
        setFormData(prev => ({ ...prev, filiere_id: '', niveau_id: '' }));
        setNiveauxFiltres([]);
      }
    }
  }, [formData.annee_universitaire_id, anneesUniversitaires, filieres]);

  // ============================================================
  // FILTRES EN CASCADE POUR LE TABLEAU
  // ============================================================

  useEffect(() => {
    let result = [...inscriptions];
    if (tableFilters.annee_universitaire_id) {
      result = result.filter(ins => {
        const val = ins.annee_universitaire_id?._id || ins.annee_universitaire_id;
        return val && String(val) === String(tableFilters.annee_universitaire_id);
      });
    }
    if (tableFilters.filiere_id) {
      result = result.filter(ins => {
        const val = ins.filiere_id?._id || ins.filiere_id;
        return val && String(val) === String(tableFilters.filiere_id);
      });
    }
    if (tableFilters.niveau_id) {
      result = result.filter(ins => {
        const val = ins.niveau_id?._id || ins.niveau_id;
        return val && String(val) === String(tableFilters.niveau_id);
      });
    }
    const terme = searchTerm.trim().toLowerCase();
    if (terme) {
      result = result.filter(ins => {
        const et = (ins.etudiant && typeof ins.etudiant === 'object')
          ? ins.etudiant
          : ((ins.etudiant_id && typeof ins.etudiant_id === 'object') ? ins.etudiant_id : null);
        const texte = [
          et?.Nom, et?.Prénom, et?.nom, et?.prenom, et?.CNE, et?.cne, et?.email,
          ins.etudiant_nom, ins.etudiant_prenom, ins.etudiant_cne, ins.etudiant_cne_masar,
          ins.filiere?.intitule, ins.filiere?.codeFiliere, ins.filiere?.libelle,
          ins.filiere_id?.intitule, ins.filiere_id?.codeFiliere, ins.filiere_id?.libelle,
          ins.niveau?.intitule, ins.niveau?.code,
          ins.niveau_id?.intitule, ins.niveau_id?.code,
          ins.anneeUniversitaire?.intitule, ins.annee_universitaire_id?.intitule,
          ins.statut
        ].filter(Boolean).join(' ').toLowerCase();
        return texte.includes(terme);
      });
    }
    setFilteredInscriptions(result);
    setTotalCount(result.length);
  }, [inscriptions, tableFilters, searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [tableFilters, searchTerm]);

  useEffect(() => {
    const anneeId = tableFilters.annee_universitaire_id;
    if (!anneeId) {
      setFilieresFiltrees([]);
      setNiveauxFiltresTable([]);
      return;
    }
    const annee = anneesUniversitaires.find(a =>
      (a._id || a.id) === anneeId || a.annee_id === anneeId
    );
    if (!annee) {
      setFilieresFiltrees([]);
      return;
    }
    const allowedAnneeIds = [annee._id, annee.annee_id, anneeId]
      .map(extractId)
      .filter(Boolean);
    const filteredList = dedupeFilieres(filieres.filter(f =>
      allowedAnneeIds.some(id => compareIds(f.annee_id, id))
    ));
    setFilieresFiltrees(filteredList);
    setTableFilters(prev => {
      if (!prev.filiere_id) return prev;
      const stillValid = filteredList.some(f => (f._id || f.id) === prev.filiere_id || f.filiere_id === prev.filiere_id);
      if (!stillValid) {
        setNiveauxFiltresTable([]);
        return { ...prev, filiere_id: '', niveau_id: '' };
      }
      return prev;
    });
  }, [tableFilters.annee_universitaire_id, anneesUniversitaires, filieres]);

  useEffect(() => {
    const filiereId = tableFilters.filiere_id;
    if (filiereId) {
      loadNiveauxTable(filiereId).then(niveauxList => {
        setNiveauxFiltresTable(niveauxList);
        setTableFilters(prev => {
          if (!prev.niveau_id) return prev;
          const stillValid = niveauxList.some(n => {
            const nId = n._id || n.id || n.niveau_id;
            return String(nId) === String(prev.niveau_id);
          });
          return stillValid ? prev : { ...prev, niveau_id: '' };
        });
      });
    } else {
      setNiveauxFiltresTable([]);
    }
  }, [tableFilters.filiere_id, loadNiveauxTable]);

  // ============================================================
  // GESTION DE LA RECHERCHE
  // ============================================================
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => setPage(0), 300);
    setSearchTimeout(timeout);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ============================================================
  // GESTION DES FILTRES
  // ============================================================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTableFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleResetFilters = () => {
    setTableFilters({
      annee_universitaire_id: '',
      filiere_id: '',
      niveau_id: '',
    });
    setFilieresFiltrees([]);
    setNiveauxFiltresTable([]);
  };

  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };

  // ============================================================
  // GESTION DU FORMULAIRE D'AFFECTATION
  // ============================================================
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleEtudiantSelect = (etudiantId) => {
    setFormData(prev => ({
      ...prev,
      etudiant_id: etudiantId
    }));
    if (formErrors.etudiant_id) {
      setFormErrors(prev => ({ ...prev, etudiant_id: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.etudiant_id) {
      errors.etudiant_id = 'L\'étudiant est requis';
    }
    if (!formData.annee_universitaire_id) {
      errors.annee_universitaire_id = 'L\'année universitaire est requise';
    }
    if (!formData.filiere_id) {
      errors.filiere_id = 'La filière est requise';
    }
    if (!formData.niveau_id) {
      errors.niveau_id = 'Le niveau est requis';
    }
    return errors;
  };

  // ============================================================
  // STEPS DU FORMULAIRE D'AFFECTATION
  // ============================================================
  const steps = [
    {
      label: 'Sélectionner l\'étudiant',
      icon: <PersonAddIcon />,
      description: 'Choisissez l\'étudiant à inscrire',
    },
    {
      label: 'Choisir la filière et le niveau',
      icon: <SchoolIcon />,
      description: 'Sélectionnez la filière et le niveau',
    },
    {
      label: 'Confirmer l\'affectation',
      icon: <AssignmentIcon />,
      description: 'Vérifiez et confirmez l\'inscription',
    },
  ];

  const handleNext = () => {
    // Valider l'étape actuelle
    if (activeStep === 0 && !formData.etudiant_id) {
      setFormErrors({ etudiant_id: 'Veuillez sélectionner un étudiant' });
      return;
    }
    if (activeStep === 1 && (!formData.filiere_id || !formData.niveau_id)) {
      if (!formData.filiere_id) {
        setFormErrors({ filiere_id: 'Veuillez sélectionner une filière' });
      }
      if (!formData.niveau_id) {
        setFormErrors({ niveau_id: 'Veuillez sélectionner un niveau' });
      }
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      etudiant_id: '',
      annee_universitaire_id: anneesUniversitaires.find(a => a.estActive === true)?._id || '',
      filiere_id: '',
      niveau_id: '',
      statut: 'EN_ATTENTE',
    });
    setNiveauxFiltres([]);
    setFormErrors({});
  };

  // ============================================================
  // OUVRIR/FERMER LES DIALOGUES
  // ============================================================
  const handleOpenDialog = (inscription = null) => {
    if (inscription) {
      setEditingInscription(inscription);
      setFormData({
        etudiant_id: inscription.etudiant_id?._id || inscription.etudiant_id || '',
        annee_universitaire_id: inscription.annee_universitaire_id?._id || inscription.annee_universitaire_id || '',
        filiere_id: inscription.filiere_id?._id || inscription.filiere_id || '',
        niveau_id: inscription.niveau_id?._id || inscription.niveau_id || '',
        statut: inscription.statut || 'EN_ATTENTE',
      });
      const filiereId = inscription.filiere_id?._id || inscription.filiere_id || '';
      if (filiereId) {
        loadNiveauxByFiliere(filiereId);
      }
      setActiveStep(2);
    } else {
      setEditingInscription(null);
      const anneeActive = anneesUniversitaires.find(a => a.estActive === true);
      setFormData({
        etudiant_id: '',
        annee_universitaire_id: anneeActive?._id || anneeActive?.id || '',
        filiere_id: '',
        niveau_id: '',
        statut: 'EN_ATTENTE',
      });
      setNiveauxFiltres([]);
      setActiveStep(0);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingInscription(null);
    setFormErrors({});
    setActiveStep(0);
  };

  const handleOpenDeleteDialog = (inscription) => {
    setDeletingInscription(inscription);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingInscription(null);
  };

  // ============================================================
  // CRUD OPERATIONS
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showNotification('Veuillez corriger les erreurs', 'error');
      return;
    }

    try {
      setLoading(true);

      if (editingInscription) {
        await axios.put(`${API_URL}/inscriptions/${editingInscription._id}`, formData);
        showNotification('Inscription modifiée avec succès', 'success');
      } else {
        await axios.post(`${API_URL}/inscriptions`, formData);
        showNotification('Inscription créée avec succès', 'success');
      }

      handleCloseDialog();
      loadInscriptions();
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors de la sauvegarde';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingInscription) return;
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/inscriptions/${deletingInscription._id}`);
      showNotification('Inscription supprimée avec succès', 'success');
      handleCloseDeleteDialog();
      loadInscriptions();
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors de la suppression';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleValider = async (inscription) => {
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/inscriptions/${inscription._id}/valider`);
      showNotification('Inscription validée avec succès', 'success');
      loadInscriptions();
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors de la validation';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnnuler = async (inscription) => {
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/inscriptions/${inscription._id}/annuler`);
      showNotification('Inscription annulée avec succès', 'success');
      loadInscriptions();
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur lors de l\'annulation';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RENDU DES ÉTAPES
  // ============================================================
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Recherchez et sélectionnez l'étudiant à inscrire
            </Typography>
            <TextField
              fullWidth
              placeholder="Rechercher par CNE, Nom ou Prénom..."
              value={etudiantSearch}
              onChange={(e) => setEtudiantSearch(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
              {etudiants
                .filter(e => {
                  const search = etudiantSearch.toLowerCase();
                  return !search || 
                    e.CNE?.toLowerCase().includes(search) ||
                    e.Nom?.toLowerCase().includes(search) ||
                    e.Prénom?.toLowerCase().includes(search);
                })
                .map((etudiant) => (
                  <Box
                    key={etudiant._id || etudiant.id}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      backgroundColor: formData.etudiant_id === (etudiant._id || etudiant.id) ? '#e3f2fd' : 'transparent',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleEtudiantSelect(etudiant._id || etudiant.id)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {etudiant.Nom} {etudiant.Prénom}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          CNE: {etudiant.CNE} | {etudiant.nationalité || 'Nationalité non spécifiée'}
                        </Typography>
                      </Box>
                      {formData.etudiant_id === (etudiant._id || etudiant.id) && (
                        <CheckCircleIcon color="primary" />
                      )}
                    </Box>
                  </Box>
                ))}
            </Paper>
            {formErrors.etudiant_id && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {formErrors.etudiant_id}
              </Typography>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Sélectionnez la filière et le niveau
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!formErrors.filiere_id}>
                  <InputLabel>Filière *</InputLabel>
                  <Select
                    name="filiere_id"
                    value={formData.filiere_id}
                    onChange={handleFormChange}
                    label="Filière *"
                    disabled={loadingReferences}
                  >
                    <MenuItem value="">
                      <em>Sélectionner une filière</em>
                    </MenuItem>
                    {filieresForm.map((filiere) => (
                      <MenuItem 
                        key={filiere._id || filiere.id} 
                        value={filiere._id || filiere.id}
                      >
                        {filiere.codeFiliere} - {filiere.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.filiere_id && (
                    <Typography variant="caption" color="error">
                      {formErrors.filiere_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth error={!!formErrors.niveau_id}>
                  <InputLabel>Niveau *</InputLabel>
                  <Select
                    name="niveau_id"
                    value={formData.niveau_id}
                    onChange={handleFormChange}
                    label="Niveau *"
                    disabled={!formData.filiere_id || loadingReferences}
                  >
                    <MenuItem value="">
                      <em>Sélectionner un niveau</em>
                    </MenuItem>
                    {niveauxFiltres.map((niveau) => (
                      <MenuItem 
                        key={niveau._id || niveau.id} 
                        value={niveau._id || niveau.id}
                      >
                        {niveau.code} - {niveau.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.niveau_id && (
                    <Typography variant="caption" color="error">
                      {formErrors.niveau_id}
                    </Typography>
                  )}
                  {!formData.filiere_id && (
                    <Typography variant="caption" color="text.secondary">
                      Sélectionnez d'abord une filière
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Année Universitaire</InputLabel>
                  <Select
                    name="annee_universitaire_id"
                    value={formData.annee_universitaire_id}
                    onChange={handleFormChange}
                    label="Année Universitaire"
                    disabled={loadingReferences}
                  >
                    {anneesUniversitaires.map((annee) => (
                      <MenuItem 
                        key={annee._id || annee.id} 
                        value={annee._id || annee.id}
                      >
                        {annee.intitule} {annee.estActive && '⭐ (Active)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="statut"
                    value={formData.statut}
                    onChange={handleFormChange}
                    label="Statut"
                  >
                    <MenuItem value="EN_ATTENTE">En attente</MenuItem>
                    <MenuItem value="CONFIRMEE">Confirmée</MenuItem>
                    <MenuItem value="ANNULEE">Annulée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Confirmez l'affectation
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Chip label="Résumé de l'inscription" size="small" />
                    </Divider>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Étudiant
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {etudiants.find(e => (e._id || e.id) === formData.etudiant_id)?.Nom || ''}
                      {' '}
                      {etudiants.find(e => (e._id || e.id) === formData.etudiant_id)?.Prénom || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      CNE: {etudiants.find(e => (e._id || e.id) === formData.etudiant_id)?.CNE || ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Année Universitaire
                    </Typography>
                    <Typography variant="body1">
                      {anneesUniversitaires.find(a => (a._id || a.id) === formData.annee_universitaire_id)?.intitule || ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Filière
                    </Typography>
                    <Typography variant="body1">
                      {filieres.find(f => (f._id || f.id) === formData.filiere_id)?.intitule || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Code: {filieres.find(f => (f._id || f.id) === formData.filiere_id)?.codeFiliere || ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Niveau
                    </Typography>
                    <Typography variant="body1">
                      {niveauxFiltres.find(n => (n._id || n.id) === formData.niveau_id)?.intitule || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Code: {niveauxFiltres.find(n => (n._id || n.id) === formData.niveau_id)?.code || ''}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Statut
                      </Typography>
                      <Chip
                        label={formData.statut === 'CONFIRMEE' ? 'Confirmée' : 
                               formData.statut === 'ANNULEE' ? 'Annulée' : 'En attente'}
                        color={formData.statut === 'CONFIRMEE' ? 'success' : 
                               formData.statut === 'ANNULEE' ? 'error' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  // ============================================================
  // RENDU
  // ============================================================
  const getStatusColor = (statut) => {
    const colors = {
      'CONFIRMEE': 'success',
      'EN_ATTENTE': 'warning',
      'ANNULEE': 'error',
    };
    return colors[statut] || 'default';
  };

  const getStatusLabel = (statut) => {
    const labels = {
      'CONFIRMEE': 'Confirmée',
      'EN_ATTENTE': 'En attente',
      'ANNULEE': 'Annulée',
    };
    return labels[statut] || statut;
  };

  const getEtudiantName = (inscription) => {
    if (inscription.etudiant) {
      return `${inscription.etudiant.Nom} ${inscription.etudiant.Prénom}`;
    }
    if (inscription.etudiant_id) {
      return inscription.etudiant_id.Nom ? 
        `${inscription.etudiant_id.Nom} ${inscription.etudiant_id.Prénom}` : 
        'ID: ' + (inscription.etudiant_id._id || inscription.etudiant_id);
    }
    return 'N/A';
  };

  const getFiliereName = (inscription) => {
    if (inscription.filiere) {
      return inscription.filiere.intitule || inscription.filiere.codeFiliere;
    }
    if (inscription.filiere_id) {
      return inscription.filiere_id.intitule || inscription.filiere_id.codeFiliere || 'N/A';
    }
    return 'N/A';
  };

  const getNiveauName = (inscription) => {
    if (inscription.niveau) {
      return inscription.niveau.intitule || inscription.niveau.code;
    }
    if (inscription.niveau_id) {
      return inscription.niveau_id.intitule || inscription.niveau_id.code || 'N/A';
    }
    return 'N/A';
  };

  const getAnneeName = (inscription) => {
    if (inscription.anneeUniversitaire) {
      return inscription.anneeUniversitaire.intitule;
    }
    if (inscription.annee_universitaire_id) {
      return inscription.annee_universitaire_id.intitule || 'N/A';
    }
    return 'N/A';
  };

  // ============================================================
  // EXPORT EXCEL / PDF (meme cascade que le tableau : annee -> filiere -> niveau)
  // ============================================================
  const trouverEtudiant = (inscription) => {
    if (inscription.etudiant && typeof inscription.etudiant === 'object') return inscription.etudiant;
    if (inscription.etudiant_id && typeof inscription.etudiant_id === 'object') return inscription.etudiant_id;
    return etudiants.find(e => String(e._id || e.id) === String(inscription.etudiant_id || inscription.etudiant)) || null;
  };

  const lignesExport = () => filteredInscriptions.map(ins => {
    const et = trouverEtudiant(ins);
    return {
      cne: et?.CNE || et?.cne || et?.etudiant_cne_masar || '-',
      nom: et?.Nom || et?.nom || et?.etudiant_nom || '-',
      prenom: et?.Prénom || et?.prenom || et?.etudiant_prenom || '-',
      email: et?.email || et?.etudiant_email || '-',
      telephone: et?.Téléphone || et?.telephone || et?.etudiant_tel || '-',
      filiere: getFiliereName(ins),
      niveau: getNiveauName(ins),
      annee: getAnneeName(ins),
      statut: getStatusLabel(ins.statut),
    };
  });

  const handleExportExcel = () => {
    const columns = [
      { key: 'cne', label: 'CNE' },
      { key: 'nom', label: 'Nom' },
      { key: 'prenom', label: 'Prénom' },
      { key: 'email', label: 'Email' },
      { key: 'telephone', label: 'Téléphone' },
      { key: 'filiere', label: 'Filière' },
      { key: 'niveau', label: 'Niveau' },
      { key: 'annee', label: 'Année' },
      { key: 'statut', label: 'Statut' },
    ];
    exportToExcel(lignesExport(), 'Liste_Inscrits', columns);
  };

  const handleExportPDF = async () => {
    const columns = [
      { key: 'cne', label: 'CNE' },
      { key: 'nom', label: 'Nom' },
      { key: 'prenom', label: 'Prénom' },
      { key: 'email', label: 'Email' },
      { key: 'filiere', label: 'Filière' },
      { key: 'niveau', label: 'Niveau' },
      { key: 'annee', label: 'Année' },
      { key: 'statut', label: 'Statut' },
    ];
    await exportToPDF(lignesExport(), 'Liste_des_Inscrits', columns);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: 3 }}>
        {/* En-tête */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">
            Gestion des Inscriptions
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              ({totalCount} inscriptions)
            </Typography>
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadInscriptions}
              disabled={loading}
            >
              Actualiser
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={filteredInscriptions.length === 0}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportPDF}
              disabled={filteredInscriptions.length === 0}
            >
              PDF
            </Button>
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              color={showFilters ? 'primary' : 'inherit'}
              startIcon={<FilterListIcon />}
              onClick={toggleFilters}
            >
              Filtres
            </Button>
            {peutModifierInscriptions && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Nouvelle Inscription
              </Button>
            )}
          </Box>
        </Box>

        {/* Barre de recherche */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Rechercher une inscription (étudiant, CNE, filière, année)..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filtres en cascade */}
        <Collapse in={showFilters}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Année Universitaire</InputLabel>
                  <Select
                    name="annee_universitaire_id"
                    value={tableFilters.annee_universitaire_id}
                    onChange={handleFilterChange}
                    label="Année Universitaire"
                  >
                    <MenuItem value="">
                      <em>Toutes les années</em>
                    </MenuItem>
                    {anneesUniversitaires.map((annee) => (
                      <MenuItem key={annee._id || annee.id} value={annee._id || annee.id}>
                        {annee.intitule} {annee.estActive ? '⭐' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small" disabled={!tableFilters.annee_universitaire_id}>
                  <InputLabel>Filière</InputLabel>
                  <Select
                    name="filiere_id"
                    value={tableFilters.filiere_id}
                    onChange={handleFilterChange}
                    label="Filière"
                  >
                    <MenuItem value="">
                      <em>Toutes les filières</em>
                    </MenuItem>
                    {filieresFiltrees.map((filiere) => (
                      <MenuItem key={filiere._id || filiere.id} value={filiere._id || filiere.id}>
                        {filiere.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small" disabled={!tableFilters.filiere_id}>
                  <InputLabel>Niveau</InputLabel>
                  <Select
                    name="niveau_id"
                    value={tableFilters.niveau_id}
                    onChange={handleFilterChange}
                    label="Niveau"
                  >
                    <MenuItem value="">
                      <em>Tous les niveaux</em>
                    </MenuItem>
                    {niveauxFiltresTable.map((niveau) => (
                      <MenuItem key={niveau._id || niveau.id || niveau.niveau_id} value={niveau._id || niveau.id || niveau.niveau_id}>
                        {niveau.code} - {niveau.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleResetFilters}
                  fullWidth
                >
                  Réinitialiser
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* Tableau */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell>Étudiant</TableCell>
                <TableCell>CNE</TableCell>
                <TableCell>Année</TableCell>
                <TableCell>Filière</TableCell>
                <TableCell>Niveau</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !filteredInscriptions.length ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredInscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      {searchTerm ? 'Aucun résultat pour cette recherche' : 'Aucune inscription trouvée'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInscriptions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((inscription) => (
                  <TableRow key={inscription._id}>
                    <TableCell>
                      <Typography variant="body2">
                        {getEtudiantName(inscription)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {inscription.etudiant?.CNE || inscription.etudiant_id?.CNE || '-'}
                    </TableCell>
                    <TableCell>{getAnneeName(inscription)}</TableCell>
                    <TableCell>{getFiliereName(inscription)}</TableCell>
                    <TableCell>{getNiveauName(inscription)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(inscription.statut)}
                        color={getStatusColor(inscription.statut)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(inscription.date_inscription).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {peutModifierInscriptions && inscription.statut === 'EN_ATTENTE' && (
                        <IconButton size="small" color="success" onClick={() => handleValider(inscription)}><CheckCircleIcon /></IconButton>
                      )}
                      {peutModifierInscriptions && inscription.statut === 'EN_ATTENTE' && (
                        <IconButton size="small" color="warning" onClick={() => handleAnnuler(inscription)}><CancelIcon /></IconButton>
                      )}
                      {peutModifierInscriptions && (
                        <>
                          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(inscription)}><EditIcon /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(inscription)}><DeleteIcon /></IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {/* ============================================================
          DIALOGUE D'AFFECTATION AVEC STEPS
          ============================================================ */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">
              {editingInscription ? 'Modifier l\'inscription' : 'Affecter un étudiant'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {!editingInscription ? (
            // Formulaire d'affectation avec steps
            <Box>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel StepIconComponent={() => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {step.icon}
                      </Box>
                    )}>
                      <Box>
                        <Typography variant="subtitle2">{step.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {step.description}
                        </Typography>
                      </Box>
                    </StepLabel>
                    <StepContent>
                      {renderStepContent(index)}
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          endIcon={index === steps.length - 1 ? null : <ArrowForwardIcon />}
                        >
                          {index === steps.length - 1 ? 'Terminer' : 'Suivant'}
                        </Button>
                        {index > 0 && (
                          <Button onClick={handleBack}>
                            Retour
                          </Button>
                        )}
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
              
              {activeStep === steps.length && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Affectation prête à être enregistrée !
                  </Typography>
                  <Button onClick={handleSubmit} variant="contained" color="primary">
                    Enregistrer l'affectation
                  </Button>
                  <Button onClick={handleReset} sx={{ ml: 1 }}>
                    Recommencer
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            // Formulaire d'édition simplifié
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="statut"
                    value={formData.statut}
                    onChange={handleFormChange}
                    label="Statut"
                  >
                    <MenuItem value="EN_ATTENTE">En attente</MenuItem>
                    <MenuItem value="CONFIRMEE">Confirmée</MenuItem>
                    <MenuItem value="ANNULEE">Annulée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          {editingInscription && (
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Modifier'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ============================================================
          DIALOGUE DE SUPPRESSION
          ============================================================ */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer l'inscription de l'étudiant{' '}
            <strong>{deletingInscription && getEtudiantName(deletingInscription)}</strong>
            {' '}pour l'année{' '}
            <strong>{deletingInscription && getAnneeName(deletingInscription)}</strong> ?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Inscriptions;


